import React from 'react';
import { Trash2, Upload } from 'lucide-react';
import {
  SETTINGS_BTN_DANGER_OUTLINE,
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
} from '../utils/settingsSectionStyles';
import {
  DEFAULT_LEGAL_ATTACHMENT_SETTINGS,
  LEGAL_ATTACHMENT_KINDS,
  legalAttachmentLabel,
  legalAttachmentSettingsFromUnknown,
  type LegalAttachmentKind,
  type LegalAttachmentSettings,
} from '../../leads/utils/offerLegalAttachments';
import { companyLegalAttachmentSrc } from '../../../utils/companyLegalAttachment';
import { instanceIdFromPathname } from '../../../utils/companyLogo';
import { PdfPreviewModal } from '../../templates/components/PdfPreviewModal';
import { settingsValuesEqual } from '../utils/settingsDirty';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';

const btnOutline = SETTINGS_BTN_OUTLINE;
const btnPrimary = SETTINGS_BTN_PRIMARY;
const groupTitle = SETTINGS_FIELD_LABEL;
const CHECKBOX_CLASS = 'h-4 w-4 rounded border-app-border-strong text-pipedrive-blue focus:ring-pipedrive-blue/30';

function FormCheckbox({
  id,
  label,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label htmlFor={id} className={`flex items-center gap-2.5 ${disabled ? 'cursor-default' : 'cursor-pointer'}`}>
      <input
        id={id}
        type="checkbox"
        className={CHECKBOX_CLASS}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="text-sm text-app-text">{label}</span>
    </label>
  );
}

type TargetingKey =
  | 'agb_attach_private'
  | 'agb_attach_business'
  | 'widerrufsformular_attach_private'
  | 'widerrufsformular_attach_business';

type PendingLegalFile = File | 'remove' | null;
type PendingLegalFiles = Record<LegalAttachmentKind, PendingLegalFile>;

const EMPTY_PENDING: PendingLegalFiles = { agb: null, widerrufsformular: null };
const MAX_LEGAL_PDF_BYTES = 10 * 1024 * 1024;

function targetingFromSettings(s: LegalAttachmentSettings) {
  return {
    agb_attach_private: s.agb_attach_private,
    agb_attach_business: s.agb_attach_business,
    widerrufsformular_attach_private: s.widerrufsformular_attach_private,
    widerrufsformular_attach_business: s.widerrufsformular_attach_business,
  };
}

function fileFieldsFromSettings(s: LegalAttachmentSettings) {
  return {
    has_agb: s.has_agb,
    agb_filename: s.agb_filename,
    has_widerrufsformular: s.has_widerrufsformular,
    widerrufsformular_filename: s.widerrufsformular_filename,
  };
}

function displayFile(
  kind: LegalAttachmentKind,
  form: LegalAttachmentSettings,
  pending: PendingLegalFiles,
): { hasFile: boolean; filename: string | null } {
  const change = pending[kind];
  if (change instanceof File) return { hasFile: true, filename: change.name };
  if (change === 'remove') return { hasFile: false, filename: null };
  if (kind === 'agb') return { hasFile: form.has_agb, filename: form.agb_filename };
  return { hasFile: form.has_widerrufsformular, filename: form.widerrufsformular_filename };
}

export function LegalAttachmentsSettingsSection() {
  const [form, setForm] = React.useState<LegalAttachmentSettings>(DEFAULT_LEGAL_ATTACHMENT_SETTINGS);
  const [editSnapshot, setEditSnapshot] = React.useState<LegalAttachmentSettings | null>(null);
  const [loaded, setLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  const [bust, setBust] = React.useState(0);
  const [preview, setPreview] = React.useState<{ kind: LegalAttachmentKind; fileName: string } | null>(null);
  const [pending, setPending] = React.useState<PendingLegalFiles>(EMPTY_PENDING);
  const agbInputRef = React.useRef<HTMLInputElement>(null);
  const widerrufInputRef = React.useRef<HTMLInputElement>(null);
  const urlInstanceId = instanceIdFromPathname(typeof window !== 'undefined' ? window.location.pathname : '');
  const editing = editSnapshot != null;

  const pendingPreviewUrls = React.useMemo(() => {
    const urls: Partial<Record<LegalAttachmentKind, string>> = {};
    for (const kind of LEGAL_ATTACHMENT_KINDS) {
      const change = pending[kind];
      if (change instanceof File) urls[kind] = URL.createObjectURL(change);
    }
    return urls;
  }, [pending]);

  React.useEffect(() => {
    return () => {
      Object.values(pendingPreviewUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [pendingPreviewUrls]);

  const applyCompany = (data: unknown) => {
    setForm(legalAttachmentSettingsFromUnknown(data));
  };

  const applyPersistedFileFields = (data: unknown) => {
    const files = fileFieldsFromSettings(legalAttachmentSettingsFromUnknown(data));
    setForm((current) => ({ ...current, ...files }));
    setBust((n) => n + 1);
  };

  React.useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setLoadError(null);
    fetch('/api/settings/company', { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error || 'Einstellungen konnten nicht geladen werden.');
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        applyCompany(data);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Einstellungen konnten nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const beginEdit = () => {
    setEditSnapshot({ ...form });
    setPending(EMPTY_PENDING);
    setError(null);
    setSaved(false);
  };

  const cancelEdit = () => {
    if (editSnapshot) {
      setForm((current) => ({
        ...editSnapshot,
        ...fileFieldsFromSettings(current),
      }));
    }
    setPending(EMPTY_PENDING);
    setEditSnapshot(null);
    setPreview(null);
    setError(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      for (const kind of LEGAL_ATTACHMENT_KINDS) {
        const change = pending[kind];
        if (change instanceof File) {
          const fd = new FormData();
          fd.append('file', change);
          const res = await fetch(`/api/settings/company/legal-attachments/${kind}`, {
            method: 'POST',
            body: fd,
            credentials: 'include',
          });
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          if (!res.ok) throw new Error(body.error || 'Upload fehlgeschlagen.');
          applyPersistedFileFields(body);
          setPending((prev) => ({ ...prev, [kind]: null }));
        } else if (change === 'remove') {
          const res = await fetch(`/api/settings/company/legal-attachments/${kind}`, {
            method: 'DELETE',
            credentials: 'include',
          });
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          if (!res.ok) throw new Error(body.error || 'Datei konnte nicht entfernt werden.');
          applyPersistedFileFields(body);
          setPending((prev) => ({ ...prev, [kind]: null }));
        }
      }
      const res = await fetch('/api/settings/company', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agb_attach_private: form.agb_attach_private,
          agb_attach_business: form.agb_attach_business,
          widerrufsformular_attach_private: form.widerrufsformular_attach_private,
          widerrufsformular_attach_business: form.widerrufsformular_attach_business,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || 'Einstellungen konnten nicht gespeichert werden.');
      }
      applyCompany(await res.json());
      setBust((n) => n + 1);
      setPending(EMPTY_PENDING);
      setEditSnapshot(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Einstellungen konnten nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const onFile = (kind: LegalAttachmentKind, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const mime = String(file.type || '').toLowerCase();
    if (mime !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError('Nur PDF-Dateien sind erlaubt.');
      return;
    }
    if (file.size > MAX_LEGAL_PDF_BYTES) {
      setError('PDF, max. 10 MB.');
      return;
    }
    setError(null);
    setPending((prev) => ({ ...prev, [kind]: file }));
  };

  const removeFile = (kind: LegalAttachmentKind) => {
    const hadSaved = kind === 'agb' ? form.has_agb : form.has_widerrufsformular;
    setError(null);
    setPending((prev) => ({ ...prev, [kind]: hadSaved ? 'remove' : null }));
  };

  const setTargeting = (key: TargetingKey, value: boolean) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const disabled = !editing || !loaded || saving;
  const filesDirty = pending.agb != null || pending.widerrufsformular != null;
  const dirty =
    editing &&
    editSnapshot != null &&
    (filesDirty || !settingsValuesEqual(targetingFromSettings(form), targetingFromSettings(editSnapshot)));
  const agbDisplay = displayFile('agb', form, pending);
  const widerrufDisplay = displayFile('widerrufsformular', form, pending);

  const previewFileUrl = preview
    ? pending[preview.kind] instanceof File
      ? pendingPreviewUrls[preview.kind] ?? null
      : pending[preview.kind] === 'remove'
        ? null
        : companyLegalAttachmentSrc(urlInstanceId, preview.kind, bust)
    : null;

  const renderColumn = (
    kind: LegalAttachmentKind,
    hasFile: boolean,
    filename: string | null,
    privateKey: TargetingKey,
    businessKey: TargetingKey,
    privateChecked: boolean,
    businessChecked: boolean,
    inputRef: React.RefObject<HTMLInputElement | null>,
  ) => {
    const label = legalAttachmentLabel(kind);
    return (
      <div className="min-w-0 space-y-6">
        <div>
          <p className={`${groupTitle} mb-3`}>{label}</p>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => onFile(kind, e)}
          />
          {hasFile ? (
            <div className="text-sm text-app-text truncate">
              <button
                type="button"
                onClick={() => setPreview({ kind, fileName: filename || `${label}.pdf` })}
                className="text-pipedrive-blue hover:underline text-left truncate max-w-full"
              >
                {filename || `${label}.pdf`}
              </button>
            </div>
          ) : (
            <p className="text-sm text-app-text-subtle">Noch keine Datei</p>
          )}
          {editing ? (
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => inputRef.current?.click()}
                className={btnOutline}
              >
                <Upload className="w-4 h-4 text-pipedrive-blue" />
                {hasFile ? 'Ersetzen' : 'PDF hochladen'}
              </button>
              {hasFile ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => removeFile(kind)}
                  className={SETTINGS_BTN_DANGER_OUTLINE}
                >
                  <Trash2 className="w-4 h-4" />
                  Entfernen
                </button>
              ) : null}
              <p className="w-full text-xs text-app-text-subtle">PDF, max. 10&nbsp;MB.</p>
            </div>
          ) : null}
        </div>
        <div>
          <p className={`${groupTitle} mb-3`}>An Angebote anhängen</p>
          <div className="space-y-2.5">
            <FormCheckbox
              id={`${kind}-private`}
              label="Für Privatkunden"
              checked={privateChecked}
              disabled={disabled || !hasFile}
              onChange={(v) => setTargeting(privateKey, v)}
            />
            <FormCheckbox
              id={`${kind}-business`}
              label="Für Geschäftskunden"
              checked={businessChecked}
              disabled={disabled || !hasFile}
              onChange={(v) => setTargeting(businessKey, v)}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
    <SettingsCard
      title="AGB & Widerruf"
      description="Wird automatisch an passende Angebote angehängt. Im Editor unter Anhänge pro Vorgang entfernbar."
      actions={
        <>
          <SettingsCardSavedStatus saved={saved && !editing} />
          {editing ? (
            <>
              <button type="button" className={btnOutline} onClick={cancelEdit} disabled={saving}>
                Abbrechen
              </button>
              <button
                type="button"
                className={btnPrimary}
                disabled={!loaded || saving || !dirty}
                onClick={() => void save()}
              >
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
            </>
          ) : (
            <button type="button" className={btnOutline} onClick={beginEdit} disabled={!loaded}>
              Bearbeiten
            </button>
          )}
        </>
      }
    >
      {loadError ? <p className="mb-4 text-sm text-red-600">{loadError}</p> : null}
      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}

      <div className="grid gap-6 sm:grid-cols-2">
        {renderColumn(
          'agb',
          agbDisplay.hasFile,
          agbDisplay.filename,
          'agb_attach_private',
          'agb_attach_business',
          form.agb_attach_private,
          form.agb_attach_business,
          agbInputRef,
        )}
        {renderColumn(
          'widerrufsformular',
          widerrufDisplay.hasFile,
          widerrufDisplay.filename,
          'widerrufsformular_attach_private',
          'widerrufsformular_attach_business',
          form.widerrufsformular_attach_private,
          form.widerrufsformular_attach_business,
          widerrufInputRef,
        )}
      </div>
    </SettingsCard>
    <PdfPreviewModal
      open={preview != null && Boolean(previewFileUrl)}
      fileUrl={previewFileUrl}
      fileName={preview?.fileName || 'dokument.pdf'}
      onClose={() => setPreview(null)}
    />
    </>
  );
}
